import React, { useCallback, useMemo } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { AgGrid, AgGridReact, Grid } from "@jsq/ds/core";
import {
  ComplianceCaseTypeEnum as CaseType,
  ComplianceCaseStatus as CaseStatus,
} from "~/graphql/supergraphEnums";
import {
  type ComplianceCaseStatus,
  type ComplianceCaseTypeEnum,
} from "~/graphql/supergraphTypes";
import { useReusableProfilesFeatureFlag } from "~/pages/Compliance/flags";
import { CellLinkRenderer } from "./CellLinkRenderer";
import GroupInnerRenderer from "./GroupInnerRenderer";
import { useImpactedGPContext } from "./impactedGPcontext";
import { ImpactedGPGraphqlDataSource } from "./impactedGPGraphqlDataSource";
import { ImpactedGPRow } from "./types";

interface ImpactedGPGridProps {
  caseId: number;
  caseType: ComplianceCaseTypeEnum;
  profileId: number;
  caseStatus: ComplianceCaseStatus;
}

type DetailRowParams = {
  data: ImpactedGPRow;
  successCallback: (rows: ImpactedGPRow["details"]) => void;
};

const ImpactedGPGrid: React.FC<ImpactedGPGridProps> = ({
  caseId,
  caseType,
  profileId,
  caseStatus,
}) => {
  const { setCount } = useImpactedGPContext();
  const useReusableProfiles = useReusableProfilesFeatureFlag();
  const handleCountChange = useCallback(
    (count: number) => {
      if (setCount) {
        setCount(count);
      }
    },
    [setCount],
  );

  const dataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    return new ImpactedGPGraphqlDataSource(
      caseId,
      caseType,
      profileId,
      handleCountChange,
      useReusableProfiles,
    );
  }, [caseId, caseType, profileId, handleCountChange, useReusableProfiles]);

  const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "GP",
      headerName: "Client",
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        innerRenderer: GroupInnerRenderer,
      },
      width: 280,
    },
    {
      field: "case",
      headerName: "Escalation case",
      cellRenderer: (params: ICellRendererParams) => <CellLinkRenderer {...params} />,
      hide: !(
        caseType === CaseType.Escalation ||
        (caseType === CaseType.RecurringHit && caseStatus === CaseStatus.Closed)
      ),
    },
    {
      field: "position",
      headerName: "Impacted positions",
    },
    {
      field: "date",
      headerName: "Date of generation",
      hide: !(
        caseType === CaseType.Escalation ||
        (caseType === CaseType.RecurringHit && caseStatus === CaseStatus.Closed)
      ),
    },
  ];

  const detailsColumnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "positionName",
      headerName: "Position",
      cellRenderer:
        caseType === CaseType.InvestorOnboarding ||
        caseType === CaseType.PaymentInstructionChangeReview
          ? undefined
          : (params: ICellRendererParams) => <CellLinkRenderer {...params} />,
      valueGetter:
        caseType === CaseType.InvestorOnboarding ||
        caseType === CaseType.PaymentInstructionChangeReview
          ? (params) => params.data?.positionName?.name
          : undefined,
    },
    {
      field: "program",
      headerName: "Compliance program",
    },
  ];

  return (
    <div
      data-testid="impacted-grid"
      data-props={JSON.stringify({ caseId, caseType, profileId, caseStatus })}
    >
      <Grid<ImpactedGPRow>
        columnDefs={columnDefs}
        domLayout="autoHeight"
        autoSizeStrategy={{ type: "fitGridWidth" }}
        context={{ drawerCaseId: caseId }}
        //detailCell
        masterDetail={true}
        detailRowAutoHeight={true}
        detailCellRendererParams={{
          detailGridOptions: {
            columnDefs: detailsColumnDefs,
            domLayout: "normal",
            autoSizeStrategy: { type: "fitGridWidth" },
          },
          getDetailRowData: (params: DetailRowParams) => {
            params.successCallback(params.data.details);
          },
        }}
        rowModelType="serverSide"
        serverSideDatasource={dataSource}
      />
    </div>
  );
};

export default ImpactedGPGrid;
